import { type AdminRefusalCode, adminRefusalSchema } from '@matter_extractor/shared';
import { z } from 'zod';
import { ApiError } from './api';

/**
 * Every French sentence the administration door says (auth Tahiti spec §5/§6).
 *
 * A `Record` on the codes `shared` declares, régime `lib/model-labels.ts`: a
 * refusal added to the wire contract without a phrase here STOPS COMPILING.
 * The server `message` never reaches an operator - it is an English developer
 * string that belongs to the logs and to OpenAPI (variantes §11.2).
 */

/**
 * The four refusals, and none of them is interchangeable with another.
 *
 * The 401 is deliberately vague about WHICH half failed, and that is the
 * server's decision reproduced here rather than softened: naming the address
 * would tell whoever is trying whether it belongs to an operator.
 *
 * The 503 is the one that matters most. It is the screen an operator meets
 * during a Tahiti outage, and it is the only place the break-glass is named -
 * without that sentence, somebody retypes a correct password three times, then
 * doubts their own account, then loses half a day. It says the shared token
 * exists without saying what it is.
 */
export const ADMIN_REFUSAL_WORDING: Record<AdminRefusalCode, string> = {
  'invalid-credentials': 'Adresse ou mot de passe refusé.',
  'email-not-verified':
    'Cette adresse n’est pas encore vérifiée. Validez le courriel de confirmation, puis réessayez.',
  'too-many-attempts':
    'Trop de tentatives depuis ce poste. Attendez quelques minutes avant de réessayer.',
  'identity-unavailable':
    'Le service d’identité est indisponible : votre compte ne peut pas être vérifié pour le moment. ' +
    'Si l’accès est urgent, les écrans s’ouvrent encore avec le jeton d’administration partagé.',
};

/**
 * The three states of the door, and the third is the one this lot adds.
 *
 * « On n’a jamais demandé » is silence, not a message. « Ce que vous avez tapé
 * n’est pas ça » is a refusal. « Votre session a expiré » is NEITHER: nobody
 * mistyped anything, and wording it as a mistake makes an operator check a
 * password that was right all along. The sentence is `mss-creator`'s, taken as
 * it stands rather than reinvented.
 */
export const ADMIN_DOOR_WORDING = {
  emailLabel: 'Adresse professionnelle',
  passwordLabel: 'Mot de passe',
  /**
   * The ONE placeholder of the card, and the password deliberately has none
   * (issue #35).
   *
   * A placeholder is not a label and never replaces one - both fields keep
   * theirs above them. It shows the SHAPE expected, which is the one thing a
   * label cannot say without getting long, and an address has a shape.
   *
   * The reference card puts `••••••••` in the password field. That placeholder
   * makes the eye look BROKEN: reveal an empty field and eight dots are still
   * there, so the one gesture whose whole purpose is « show me what I typed »
   * appears not to work. A password has no shape to show anyway.
   */
  emailPlaceholder: 'vous@exemple.fr',
  /**
   * What the door is FOR, under the name of the screen it opens (issue #35).
   *
   * The heading is read from the catalogue and names a destination - « Journal
   * des sessions », « Entretiens à l'aveugle ». Alone it reads like the title of
   * the screen behind, which is exactly what somebody who is not signed in does
   * not have. This line says what the card actually is.
   */
  subtitle: 'Accédez à l’administration du dispositif.',
  /**
   * The way out of a door that will not open, and it is deliberately NOT a
   * link (issue #35).
   *
   * The reference card of `words-interface` renders this as a button wired to
   * `() => undefined`, and the checkbox beside it is the other dead control the
   * same file ships. A control that does nothing is a lie told by an interface -
   * the argument this door already makes about « se souvenir de moi » - so what
   * is kept is the SENTENCE, which is what an operator needed: eight people
   * share one deployment and the administrator is a colleague they can reach.
   *
   * No address either: `CHAT_CONTACT_EMAIL` is where a guest asks for an
   * erasure, and pointing an operator at it would send a lock-out to the wrong
   * inbox. Inventing a second address is worse than naming a person.
   */
  help: 'Besoin d’aide ? Contactez votre administrateur.',
  /**
   * The two names of the eye, and they carry a trap worth naming (issue #35).
   *
   * The name changes rather than the state: this is a button that renames
   * itself to the next action, not a toggle announcing `aria-pressed`. Both are
   * legitimate, and doing BOTH double-announces - a screen reader says
   * « masquer le mot de passe, activé », which reads as the opposite of what
   * the field is doing.
   *
   * These strings reach the accessibility tree through visually hidden TEXT
   * inside the button, never through `aria-label`, and that is load-bearing:
   * `admin-login.spec.ts` fills the field with `getByLabel(/mot de passe/i)`
   * and the frontend suite with `getByLabelText(/mot de passe/i)`. Both match an
   * `aria-label` on any element, so an eye labelled that way would resolve TWO
   * elements and break the two suites on a strict-mode violation. Inner text is
   * matched by the accessible NAME instead (`getByRole('button', { name })`),
   * which is what the capture script clicks.
   */
  showPassword: 'Afficher le mot de passe',
  /**
   * The name of the package's password toggle, which it sets as an `aria-label` (and keeps while
   * pressed). « mot de passe » must not be in it, for the reason above.
   */
  passwordToggle: 'Afficher la saisie',
  hidePassword: 'Masquer le mot de passe',
  submitting: 'Connexion…',
  expired: 'Votre session a expiré. Reconnectez-vous pour continuer.',
  /**
   * The last resort, for a failure that carries no code at all - a network that
   * dropped, a proxy that answered HTML. It says what to do rather than what
   * happened, because there is nothing honest to say about what happened.
   */
  unknown: 'La connexion n’a pas abouti. Vérifiez votre réseau, puis réessayez.',
  signOut: 'Se déconnecter',
  /**
   * A machine came in by the shared token (T6). The shell says so rather than
   * inventing a name: the header names a deployment, never a person, and this
   * is the sentence that makes the mode of BUILD-918 visible on screen instead
   * of only in a `warn` nobody reads during a capture run.
   */
  machineSession: 'Session ouverte par le jeton partagé.',
} as const;

/** Names who is connected, without turning an address into a greeting. */
export function signedInAs(email: string): string {
  return `Connecté : ${email}`;
}

const refusalsSchema = z.array(adminRefusalSchema);

/**
 * The phrase for a failed login, read from the `code` and never from the
 * `message`.
 *
 * `ApiError.details` is `unknown` - it is whatever the envelope carried - so it
 * is PARSED and not cast: a refusal shape we do not know, or a proxy answering
 * something else entirely, falls back to `unknown` rather than printing a
 * developer string at an operator.
 */
export function adminRefusalWording(error: unknown): string {
  if (!(error instanceof ApiError)) return ADMIN_DOOR_WORDING.unknown;
  const parsed = refusalsSchema.safeParse(error.details);
  const code = parsed.success ? parsed.data[0]?.code : undefined;
  return code === undefined ? ADMIN_DOOR_WORDING.unknown : ADMIN_REFUSAL_WORDING[code];
}
